import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaCalendarAlt, FaDownload } from "react-icons/fa";
import styles from "../List.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import {
  CustomButton,
  CustomDropdown,
  CustomTextInput,
} from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import {
  fetchSectionOptions,
  fetchStaffClassAndSubjects,
  clearSectionOptions,
} from "../../../../redux/Masterdata";

const MonthlyAttendanceReport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const staff_id = clientData?.master_id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_year_id = clientData?.academic_year_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [formData, setFormData] = useState({
    classId: "",
    sectionId: "",
    month: "",
  });
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });
  const { translations } = useSelector((state) => state.language);

  // Get data from masterData redux slice (correct state path)
  const {
    staffClassAndSubjectOptions: reduxClassOptions,
    staffClassAndSubjectLoading: classLoading,
    sectionOptions: reduxSectionOptions,
    sectionLoading,
  } = useSelector((state) => state.masterData);

  // Build class dropdown options from redux
  // reduxClassOptions is the raw array from /school/staff/subjects API
  const classOptions = useMemo(() => {
    const seen = new Set();
    return (reduxClassOptions || [])
      .filter((item) => {
        if (seen.has(item.class_id)) return false;
        seen.add(item.class_id);
        return true;
      })
      .map((item) => ({
        value: item.class_id,
        label: item.class_name,
      }));
  }, [reduxClassOptions]);

  // Build section dropdown options from redux
  // reduxSectionOptions already has { value, label } from the thunk
  const sectionOptions = useMemo(
    () => reduxSectionOptions || [],
    [reduxSectionOptions],
  );

  // Static month options
  const monthOptions = useMemo(
    () => [
      { label: translations.labels?.january || "January", value: 1 },
      { label: translations.labels?.february || "February", value: 2 },
      { label: translations.labels?.march || "March", value: 3 },
      { label: translations.labels?.april || "April", value: 4 },
      { label: translations.labels?.may || "May", value: 5 },
      { label: translations.labels?.june || "June", value: 6 },
      { label: translations.labels?.july || "July", value: 7 },
      { label: translations.labels?.august || "August", value: 8 },
      { label: translations.labels?.september || "September", value: 9 },
      { label: translations.labels?.october || "October", value: 10 },
      { label: translations.labels?.november || "November", value: 11 },
      { label: translations.labels?.december || "December", value: 12 },
    ],
    [translations],
  );

  // Fetch staff class list on mount
  useEffect(() => {
    if (staff_id) {
      dispatch(
        fetchStaffClassAndSubjects({
          staff_id: parseInt(staff_id),
          class_id: "",
          section_id: "",
          subject_id: "",
        }),
      );
    }
  }, [dispatch, staff_id]);

  // Fetch section options when class changes
  useEffect(() => {
    if (formData.classId) {
      dispatch(
        fetchSectionOptions({
          id: [parseInt(formData.classId)],
          school_id: parseInt(school_id),
          staff_id: parseInt(staff_id),
        }),
      );
    } else {
      dispatch(clearSectionOptions());
    }
  }, [dispatch, formData.classId, school_id]);

  // Auto-fetch report when all filters are selected
  useEffect(() => {
    if (formData.classId && formData.sectionId && formData.month) {
      fetchMonthlyReport();
    }
  }, [formData.classId, formData.sectionId, formData.month]);

  // Fetch monthly attendance report from API
  const fetchMonthlyReport = async () => {
    setLoading(true);
    try {
      const payload = {
        academic_year: parseInt(academic_year_id),
        class_id: parseInt(formData.classId),
        section_id: formData.sectionId,
        month: parseInt(formData.month),
      };

      const response = await AxiosInstance.post(
        `/attendance/staff/attendance/monthly-report`,
        payload,
      );

      if (response?.data?.success || response?.data?.status) {
        const reportData = response.data.data?.rows || [];
        const transformedData = reportData.map((item, index) => ({
          id: item.student_id,
          sno: index + 1,
          student_id: item.student_id,
          student_name: item.student_name,
          total_present: item.total_present || "0",
          total_absent: item.total_absent || "0",
          total_leave: item.total_leave || "0",
          total_working_days: item.total_working_days || 0,
          attendance_percentage: item.attendance_percentage || "0.00",
        }));
        setData(transformedData);
        setRowCount(transformedData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching monthly attendance report:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle dropdown changes with proper cascading resets
  const handleDropdownChange = (name, selectedOption) => {
    const value = selectedOption?.value ?? selectedOption ?? "";

    if (name === "classId") {
      // Reset section, month, and table data when class changes
      setFormData((prev) => ({
        ...prev,
        classId: value,
        sectionId: "",
        month: "",
      }));
      setData([]);
      setRowCount(0);
    } else if (name === "sectionId") {
      // Reset month and table data when section changes
      setFormData((prev) => ({
        ...prev,
        sectionId: value,
        month: "",
      }));
      setData([]);
      setRowCount(0);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const convertToCSV = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return "";
    const headers = [
      "Student",
      "Present",
      "Absent",
      "Leave",
      "Working Days",
      "Attendance (%)",
    ];

    const rows = dataArray.map((student) => {
      return [
        student.student_name || "",
        student.total_present || "",
        student.total_absent || "",
        student.total_leave || "",
        student.total_working_days || "",
        student.attendance_percentage || "",
      ]
        .map((field) => {
          const stringField = String(field);
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          if (
            stringField.includes(",") ||
            stringField.includes('"') ||
            stringField.includes("\n")
          ) {
            return `"${stringField.replace(/"/g, '""')}"`;
          }
          return stringField;
        })
        .join(",");
    });

    return [headers.join(","), ...rows].join("\n");
  };

  const downloadCSV = (csvContent, fileName) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  // Download Excel
  const handleDownloadExcel = async () => {
    if (!formData.classId || !formData.sectionId || !formData.month) return;
    try {
      setDownloadLoading(true);
      let response;
      const payload = {
        academic_year: parseInt(academic_year_id),
        class_id: parseInt(formData.classId),
        section_id: formData.sectionId,
        month: parseInt(formData.month),
      };
      response = await AxiosInstance.post(
        `/attendance/staff/attendance/monthly-report`,
        payload,
      );
      if (response.data?.status) {
        const students = response.data.data.rows || [];
        console.log(students, "studentsstudentsstudents");

        if (students.length === 0) {
          setModalState({
            open: true,
            type: "success",
            message: response.data?.message || "No data available to download.",
            redirectPath: "",
          });
          return;
        }
        const csvContent = convertToCSV(students);
        downloadCSV(csvContent, "MONTHLY_REPORT.csv");
      } else {
        setModalState({
          open: true,
          type: "success",
          message: response.data?.message || "No data available to download.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error downloading students:", error);
      setModalState({
        open: true,
        type: "error",
        message: "Failed to download student list. Please try again.",
        redirectPath: "",
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!apiSearch) return data;
    const searchLower = apiSearch.toLowerCase();
    return data.filter(
      (item) =>
        item.student_name?.toLowerCase().includes(searchLower) ||
        String(item.student_id)?.toLowerCase().includes(searchLower),
    );
  }, [data, apiSearch]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, pagination.pageIndex, pagination.pageSize]);

  // Table columns matching the image
  const columns = useMemo(
    () => [
      {
        accessorKey: "student_name",
        header: translations.labels?.student || "Student",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #f5a623, #f7c948)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "14px",
                flexShrink: 0,
              }}
            >
              {row.original.student_name?.charAt(0)?.toUpperCase() || "S"}
            </div>
            <div>
              <div style={{ fontSize: "13px", color: "#666" }}>
                {row.original.student_id}
              </div>
              <div style={{ fontSize: "14px", fontWeight: 500, color: "#333" }}>
                {row.original.student_name}
              </div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: "total_present",
        header: translations.labels?.present || "Present",
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
      },
      {
        accessorKey: "total_absent",
        header: translations.labels?.absent || "Absent",
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
      },
      {
        accessorKey: "total_leave",
        header: translations.labels?.leave || "Leave",
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
      },
      {
        accessorKey: "total_working_days",
        header: translations.labels?.workingDays || "Working Days",
        enableSorting: true,
        enableColumnFilter: false,
        size: 120,
      },
      {
        accessorKey: "attendance_percentage",
        header: translations.labels?.attendancePercentage || "Attendance(%)",
        enableSorting: true,
        enableColumnFilter: false,
        size: 130,
      },
    ],
    [translations],
  );
  const handleSearchRemove = () => {
    setSearchInput(""); // Clear input box
    setApiSearch(""); // Clear filter logic
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0, // Reset to first page
    }));
  };
  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaCalendarAlt />
        </div>
        <h1 className={styles.headerTitle}>
          {translations.labels?.studentAttendanceMonthlyView ||
            "Student Attendance Monthly View"}
        </h1>
      </div>

      {/* Filters */}
      <div className={styles.filterSection}>
        <div className="row align-items-end">
          {/* Batch / Academic Year */}
          <div className="col-lg-2 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.batch || "Batch"}
              name="academicYear"
              type="text"
              value={academic_year}
              placeholder={
                translations.labels?.selectAcademicYear ||
                "Select Academic Year"
              }
              disabled
              readOnly
            />
          </div>

          {/* Class Dropdown */}
          <div className="col-lg-2 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels?.classLabel || "Class"}
              name="classId"
              value={formData.classId}
              onChange={(value) => handleDropdownChange("classId", value)}
              options={classOptions}
              placeholder={
                translations.labels?.classPlaceholder || "Select Class"
              }
              className={styles.dropValueStyles}
              loading={classLoading}
            />
          </div>

          {/* Section Dropdown */}
          <div className="col-lg-2 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels?.sectionLabel || "Section"}
              name="sectionId"
              className={styles.dropValueStyles}
              value={formData.sectionId}
              onChange={(value) => handleDropdownChange("sectionId", value)}
              options={sectionOptions}
              placeholder={
                translations.labels?.sectionPlaceholder || "Select Section"
              }
              disabled={!formData.classId}
              loading={sectionLoading}
            />
          </div>

          {/* Month Dropdown */}
          <div className="col-lg-2 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels?.month || "Month"}
              name="month"
              className={styles.dropValueStyles}
              value={formData.month}
              onChange={(value) => handleDropdownChange("month", value)}
              options={monthOptions}
              placeholder={translations.labels?.selectMonth || "Select Month"}
              disabled={!formData.sectionId}
            />
          </div>

          {/* Download Excel Button */}
          <div className="col-lg-3 px-2 col-md-6 col-sm-12 mb-4">
            <CustomButton
              type="button"
              variant="contained"
              className={styles.downloadButton}
              fullWidth={false}
              onClick={handleDownloadExcel}
              disabled={
                !formData.classId || !formData.sectionId || !formData.month
              }
            >
              <FaDownload style={{ marginRight: "6px" }} />
              {translations.labels?.downloadExcel || "DOWNLOAD EXCEL"}
            </CustomButton>
          </div>
        </div>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={paginatedData}
        loading={loading}
        pagination={pagination}
        rowCount={filteredData.length}
        onPaginationChange={setPagination}
        placeholder={translations.labels.searchButton}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
        handleSearchRemove={handleSearchRemove}
      />
    </div>
  );
};

export default MonthlyAttendanceReport;
